import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import {
  autoUpdate,
  flip,
  offset,
  size,
  useFloating,
} from '@floating-ui/react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { CheckIcon, SearchIcon } from '../../main';

import { useTranslation } from 'react-i18next';

export type DropdownOption = {
  id: string;
  label: React.ReactNode;
  isHighlighted?: boolean;
  disabled?: boolean;
  queryString?: string;
};

export type DropdownOptionGroup = {
  name: string;
  options: DropdownOption[];
  isShowAllOptions?: boolean;
  numberOfOptions?: number;
  hideHeader?: boolean;
};

/**
 * Opt-in windowing for callers whose option list is long enough that mounting
 * every row costs a frame, and for callers that page their options in as the
 * list is scrolled. Off by default: every other caller renders as before.
 */
export type DropdownVirtualization = {
  enabled: boolean;
  /** Fired when the reader reaches the end of the loaded rows. */
  onEndReached?: () => void;
  /** Rendered after the last row, for a loading or end-of-list line. */
  footer?: React.ReactNode;
};

export interface DropdownWithSearchInputProps {
  onOptionSelect: (option: DropdownOption) => void;
  onQueryChange: (newQuery: string) => void;
  optionGroups: DropdownOptionGroup[];
  query: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isSingleSelection?: boolean;
  isShowLimitedOptions?: boolean;
  placeholder?: string;
  onClear?: () => void;
  /** Accessible name for the clear button. Default: 'Clear selection'. */
  clearLabel?: string;
  /** Close the list after a pick. Defaults to `isSingleSelection`. */
  closeOnSelect?: boolean;
  virtualization?: DropdownVirtualization;
  /** Tallest the option panel gets, in px. Default: two thirds of the viewport. */
  maxListHeight?: number;
  /**
   * Stop an Escape that closes the list from reaching the document, for an
   * input inside a dialog that also closes on Escape. Off by default.
   */
  containEscape?: boolean;
  'aria-describedby'?: string;
}

/** One rendered line of a virtualized dropdown: a group heading or an option. */
type FlatItem =
  | { kind: 'header'; group: DropdownOptionGroup }
  | { kind: 'option'; option: DropdownOption; globalIndex: number };

const NUMBER_OF_LIMITED_OPTIONS_TO_SHOW = 5;

/** Tallest the windowed option list gets before it scrolls internally. */
const VIRTUAL_LIST_MAX_HEIGHT = 320;

export const DropdownWithSearchInput = ({
  onClear,
  onOptionSelect,
  onQueryChange,
  optionGroups,
  query,
  icon,
  isSingleSelection,
  isShowLimitedOptions,
  placeholder = 'Search...',
  clearLabel = 'Clear selection',
  closeOnSelect,
  virtualization,
  maxListHeight,
  containEscape = false,
  'aria-describedby': ariaDescribedBy,
}: DropdownWithSearchInputProps) => {
  const { t } = useTranslation('designSystem');
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const isVirtualized = Boolean(virtualization?.enabled);
  const shouldCloseOnSelect = closeOnSelect ?? Boolean(isSingleSelection);
  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  // The windowed list owns its own scroll container and needs a concrete height: the
  // panel's height comes from its content, so pointing the list at the panel
  // would leave both at zero. The list reports its full content height and
  // the panel caps it.
  const [listHeight, setListHeight] = useState(0);
  const [internalOptionGroups, setInternalOptionGroups] = useState<
    DropdownOptionGroup[]
  >([...optionGroups]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);

  // The panel is portaled to <body>, so it must be repositioned as the page's
  // nested containers scroll; autoUpdate tracks every overflow ancestor of the
  // input. flip and size keep the panel on screen when the input sits low.
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(
              availableHeight,
              maxListHeight ?? window.innerHeight * 0.67,
            )}px`,
          });
        },
      }),
    ],
  });

  // Helper function to get visible options from a group
  const getVisibleOptions = useMemo(
    () => (group: DropdownOptionGroup) => {
      if (
        !isShowLimitedOptions ||
        group.isShowAllOptions ||
        group.options.length <= NUMBER_OF_LIMITED_OPTIONS_TO_SHOW
      ) {
        return group.options;
      }
      return group.options.slice(0, NUMBER_OF_LIMITED_OPTIONS_TO_SHOW);
    },
    [isShowLimitedOptions],
  );

  // Flatten all visible options for keyboard navigation
  const allVisibleOptions = useMemo(
    () => internalOptionGroups.flatMap((group) => getVisibleOptions(group)),
    [internalOptionGroups, getVisibleOptions],
  );

  // Save the index of each group
  const groupIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    internalOptionGroups.forEach((group, index) => {
      map.set(group.name, index);
    });
    return map;
  }, [internalOptionGroups]);

  // Save the cumulative number of options before each group
  const cumulativeOptionCountsByGroupIndex: number[] = useMemo(() => {
    const counts: number[] = [0]; // There are no options before the first group
    let cumulative = 0;
    for (let i = 0; i < internalOptionGroups.length; i++) {
      cumulative += getVisibleOptions(internalOptionGroups[i]).length;
      counts.push(cumulative);
    }
    return counts;
  }, [internalOptionGroups, getVisibleOptions]);

  // Calculate the global index
  const getGlobalIndex = useCallback(
    ({ name }: DropdownOptionGroup, groupIndex: number) => {
      const groupArrayIndex = groupIndexMap.get(name);
      if (groupArrayIndex === undefined) {
        return groupIndex;
      }
      return cumulativeOptionCountsByGroupIndex[groupArrayIndex] + groupIndex;
    },
    [groupIndexMap, cumulativeOptionCountsByGroupIndex],
  );

  // Group headings and options in one sequence, which is what a windowed list
  // needs: it renders a flat run of rows, not nested groups.
  const flatItems = useMemo<FlatItem[]>(() => {
    if (!isVirtualized) {
      return [];
    }
    const items: FlatItem[] = [];
    for (const group of internalOptionGroups) {
      if (!group.hideHeader) {
        items.push({ kind: 'header', group });
      }
      getVisibleOptions(group).forEach((option, groupIndex) => {
        items.push({
          kind: 'option',
          option,
          globalIndex: getGlobalIndex(group, groupIndex),
        });
      });
    }
    return items;
  }, [isVirtualized, internalOptionGroups, getVisibleOptions, getGlobalIndex]);

  // Arrow keys can land on a row that is not mounted, so the list is asked to
  // bring it into view rather than the DOM node being scrolled to.
  useEffect(() => {
    if (!isVirtualized || focusedIndex < 0) {
      return;
    }
    const itemIndex = flatItems.findIndex(
      (item) => item.kind === 'option' && item.globalIndex === focusedIndex,
    );
    if (itemIndex >= 0) {
      virtuosoRef.current?.scrollIntoView({ index: itemIndex });
    }
  }, [isVirtualized, focusedIndex, flatItems]);

  // Outside click detection — must consider both the input container *and*
  // the portaled dropdown (which lives outside the container in the DOM).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Use the event's composed path rather than `target.contains(...)`:
      // selecting an option re-renders the option list synchronously before
      // this document handler runs, detaching the clicked node, so a
      // `contains` check on it would wrongly treat the click as outside and
      // close the panel. composedPath is captured at dispatch and survives the
      // mid-dispatch DOM change.
      const path = event.composedPath();
      const clickedInside = [containerRef.current, dropdownRef.current].some(
        (element) => element && path.includes(element),
      );
      if (clickedInside) {
        return;
      }
      setIsDropDownOpen(false);
    };
    // Use 'click' instead of 'mousedown' to avoid conflicts with HeadlessUI which calls preventDefault on mousedown events
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Sync internalOptionGroups and preserve isShowAllOptions per group
  useEffect(() => {
    setInternalOptionGroups((prevGroups) =>
      optionGroups.map((propGroup) => {
        const existingGroup = prevGroups.find(
          ({ name }) => name === propGroup.name,
        );
        return {
          ...propGroup,
          isShowAllOptions: existingGroup?.isShowAllOptions ?? false,
        };
      }),
    );
  }, [optionGroups]);

  // Reset focused index when dropdown opens/closes or options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [isDropDownOpen, optionGroups]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (isDropDownOpen) {
        if (containEscape) {
          e.stopPropagation();
        }
        setIsDropDownOpen(false);
        setFocusedIndex(-1);
      } else {
        // If Escape was pressed and the dropdown is closed, blur the input
        inputRef.current?.blur();
      }
      return;
    }

    if (!isDropDownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsDropDownOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => {
          let nextIndex = prev < allVisibleOptions.length - 1 ? prev + 1 : 0;
          let attempts = 0;
          const maxAttempts = allVisibleOptions.length;

          // Skip highlighted options by finding the next non-highlighted option
          while (
            nextIndex < allVisibleOptions.length &&
            (allVisibleOptions[nextIndex].isHighlighted ||
              allVisibleOptions[nextIndex].disabled) &&
            attempts < maxAttempts
          ) {
            nextIndex =
              nextIndex < allVisibleOptions.length - 1 ? nextIndex + 1 : 0;
            attempts++;
          }
          return nextIndex;
        });
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          let nextIndex = prev > 0 ? prev - 1 : allVisibleOptions.length - 1;
          let attempts = 0;
          const maxAttempts = allVisibleOptions.length;

          // Skip highlighted options by finding the next non-highlighted option
          while (
            nextIndex >= 0 &&
            (allVisibleOptions[nextIndex].isHighlighted ||
              allVisibleOptions[nextIndex].disabled) &&
            attempts < maxAttempts
          ) {
            nextIndex =
              nextIndex > 0 ? nextIndex - 1 : allVisibleOptions.length - 1;
            attempts++;
          }
          return nextIndex;
        });
        break;

      case 'Enter': {
        e.preventDefault();
        const indexToSelect = focusedIndex >= 0 ? focusedIndex : 0;
        const option = allVisibleOptions[indexToSelect];
        if (option && !option.isHighlighted && !option.disabled) {
          onOptionSelect(option);
          setTimeout(() => {
            if (shouldCloseOnSelect) {
              setIsDropDownOpen(false);
            } else {
              inputRef.current?.focus();
            }
          }, 0);
        }
        break;
      }

      case 'Tab':
        setIsDropDownOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const IconComponent = icon || SearchIcon;

  const renderOption = (option: DropdownOption, globalIndex: number) => {
    const { id, label, isHighlighted, disabled } = option;
    const isFocused =
      !isHighlighted && !disabled && focusedIndex === globalIndex;

    return (
      <div
        key={id}
        role="option"
        aria-disabled={isHighlighted || disabled}
        className={`my-1 flex flex-row items-center justify-between rounded p-1 text-sm ${
          isFocused
            ? 'bg-action-background'
            : disabled
              ? ''
              : 'hover:bg-action-background'
        } ${
          isHighlighted
            ? 'text-select-selected-text bg-select-selected-background'
            : disabled
              ? 'text-darker-gray cursor-not-allowed opacity-50'
              : 'cursor-pointer'
        }`}
        tabIndex={disabled ? -1 : 0}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => {
          if (!disabled) {
            setFocusedIndex(globalIndex);
          }
        }}
        onClick={() => {
          if (!isHighlighted && !disabled) {
            onOptionSelect(option);
            if (shouldCloseOnSelect) {
              setIsDropDownOpen(false);
            } else {
              // Refocus input after selection
              setTimeout(() => inputRef.current?.focus(), 0);
            }
          }
        }}
        onKeyDown={(e) => {
          if (
            (e.key === 'Enter' || e.key === ' ') &&
            !isHighlighted &&
            !disabled
          ) {
            e.preventDefault();
            onOptionSelect(option);
            if (shouldCloseOnSelect) {
              setIsDropDownOpen(false);
            }
          }
        }}
      >
        {label || id}
        {isHighlighted && <CheckIcon className="ml-1 h-3 w-3" fill="#FF732E" />}
      </div>
    );
  };

  const renderGroupHeading = (group: DropdownOptionGroup) => (
    <div
      className={`text-darkest-gray flex flex-row items-center justify-between text-sm ${
        group.hideHeader ? 'hidden' : ''
      }`}
    >
      <span id={`group-${group.name}`} className="px-1 py-2">
        {t('nameWithCount', {
          name: group.name,
          count: group.numberOfOptions || group.options.length,
        })}
      </span>
      {isShowLimitedOptions &&
        group.options.length > NUMBER_OF_LIMITED_OPTIONS_TO_SHOW && (
          <span
            className="text-darkest-gray flex cursor-pointer flex-row items-center gap-2"
            onClick={() => {
              setInternalOptionGroups((prevGroups) =>
                prevGroups.map((currentGroup) =>
                  currentGroup.name === group.name
                    ? {
                        ...currentGroup,
                        isShowAllOptions: !currentGroup.isShowAllOptions,
                      }
                    : currentGroup,
                ),
              );
            }}
          >
            {group.isShowAllOptions ? t('showLess') : t('common:showAll')}
            <ChevronRightIcon
              className={`h-3 w-3 ${group.isShowAllOptions ? 'rotate-90' : ''}`}
            />
          </span>
        )}
    </div>
  );

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        refs.setReference(node);
      }}
      className="relative min-w-80 text-sm"
    >
      <div className="relative">
        <IconComponent className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
        <input
          aria-describedby={ariaDescribedBy}
          autoComplete="off"
          spellCheck={false}
          className={`focus:border-action-primary border-light-gray placeholder:text-darker-gray w-full rounded-lg border py-2 pr-3 pl-10 outline-none placeholder:text-sm focus:ring-0`}
          data-testid="dropdown-with-search-input"
          name="dropdown-with-search-input"
          placeholder={placeholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onQueryChange(e.target.value);
            setIsDropDownOpen(true);
          }}
          // Focus alone is not enough to reopen the list: selecting an option
          // keeps focus on the input, so a caller that closes on select would
          // otherwise need a click away and back.
          onClick={() => setIsDropDownOpen(true)}
          onFocus={(e) => {
            setIsDropDownOpen(true);
            // Select existing text on focus so the user can immediately
            // overtype the current value (especially useful for
            // single-select pickers where the displayed value is the
            // selection label, and the user wants to search for a different
            // option).
            e.currentTarget.select();
          }}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          type="text"
          value={query}
        />
        {onClear && (
          <button
            aria-label={clearLabel}
            className="text-darkest-gray absolute top-1/2 right-3 flex h-5 w-5 -translate-y-1/2 items-center justify-center"
            data-testid="clear-dropdown-selection"
            onClick={onClear}
            title={clearLabel}
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {isDropDownOpen &&
        createPortal(
          <ul
            ref={(node) => {
              dropdownRef.current = node;
              refs.setFloating(node);
            }}
            aria-multiselectable={isSingleSelection ? 'false' : 'true'}
            className="z-[101] overflow-auto rounded-lg border bg-white px-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
            style={floatingStyles}
            data-testid="dropdown-with-search-input-options"
            role="listbox"
          >
            {/* A footer means more rows are on the way, so an empty list is
                not yet a "no results" answer. */}
            {internalOptionGroups.length === 0 && !virtualization?.footer && (
              <div className="text-darkest-gray px-3 py-2 text-sm">
                {t('noResultsFound')}
              </div>
            )}
            {isVirtualized ? (
              <>
                <Virtuoso
                  ref={virtuosoRef}
                  // Opens at the cap so the list has a viewport to measure
                  // in, then shrinks to the content when it reports smaller.
                  style={{
                    height:
                      listHeight > 0
                        ? Math.min(listHeight, VIRTUAL_LIST_MAX_HEIGHT)
                        : VIRTUAL_LIST_MAX_HEIGHT,
                  }}
                  totalListHeightChanged={setListHeight}
                  data={flatItems}
                  endReached={() => virtualization?.onEndReached?.()}
                  itemContent={(_index, item) =>
                    item.kind === 'header'
                      ? renderGroupHeading(item.group)
                      : renderOption(item.option, item.globalIndex)
                  }
                />
                {virtualization?.footer}
              </>
            ) : (
              internalOptionGroups.map((group) => (
                <li
                  aria-labelledby={`group-${group.name}`}
                  className="border-light-gray border-b"
                  key={group.name}
                  role="group"
                >
                  {renderGroupHeading(group)}

                  {group.options.map((option, groupIndex) => {
                    if (
                      isShowLimitedOptions &&
                      !group.isShowAllOptions &&
                      groupIndex >= NUMBER_OF_LIMITED_OPTIONS_TO_SHOW
                    ) {
                      return null;
                    }
                    return renderOption(
                      option,
                      getGlobalIndex(group, groupIndex),
                    );
                  })}
                </li>
              ))
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
};
